import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled, { IStyledComponent, RuleSet, css } from 'styled-components';

import { APIOrder, APISortOrder } from '@cd3p/core/types/api';
import { getDateSeparator } from '@cd3p/core/utils/common';
import { tooltipClasses } from '@mui/material';
import { TooltipProps } from '@mui/material/Tooltip/Tooltip';
import { styled as muiStyled } from '@mui/material/styles';
import {
  MUITable,
  OrderStatusLabel,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableSortLabel,
  Tooltip,
} from 'components';
import i18n from 'i18n';

import {
  ContentLoader,
  _,
  classNames,
  dayjs,
  useBottomScrollListener,
  useCallbackRef,
} from 'third-party';

import {
  INFINITE_SCROLL_BOTTOM_OFFSET_PX,
  INFINITE_SCROLL_DEBOUNCE_TIME_MS,
  OrdersTableColumnIds,
  TOOLTIP_APPEAR_DELAY,
} from 'constants/common';

import { Button, TableRow, Typography } from 'components/index';

import { isTitleString } from 'utils/app';

import { TableSettingsT } from 'types/app';

const COLUMN_RESIZER_WIDTH = 20;
const SCROLL_WIDTH = 30;
const SPAN_COLUMN_ID = 'span-column';

const fixedStyles = {
  position: 'sticky',
  right: 0,
  zIndex: 3,
};

const emptyTableSortHeaderStyles = {
  textDecoration: 'none',
  pointerEvents: 'none',
};

const CellTooltip = muiStyled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    whiteSpace: 'pre-line',
  },
}));

const TableWrapper = styled.div<{ minheight: string }>`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: ${props => props.minheight || '0'};
`;

const StyledTableContainer = styled(TableContainer)`
  flex-grow: 1;
`;

export const StyledTable = styled(MUITable)`
  table-layout: fixed;
`;

export const StyledTableBodyCell = styled(TableCell)<{ height: string }>`
  height: ${props => props.height};
  padding: 0 1.6rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 0;
  box-sizing: border-box;
`;

const StyledTableBody = styled(TableBody)`
  flex-grow: 1;
  overflow-y: auto;
`;

const StyledTableHeaderCell = styled(TableCell)<{ headerheight: string }>`
  height: ${props => props.headerheight};
  padding: 0 1.6rem;
  border-bottom: 1px solid ${props => props.theme.custom.palette.truegray300};
  white-space: nowrap;
  box-sizing: border-box;
  z-index: 3;
`;

export const StyledTableColumnLabel = styled(Typography)`
  font-weight: 900;
  font-size: 2rem;
  line-height: 1.25;
  text-transform: uppercase;
  color: ${props => props.theme.custom.palette.primary900};
`;

const StyledTableRow = styled(TableRow)<{ rowhighlightcolor: string }>`
  border: 1px solid #d4d4d4;
  padding: 0;
  background-color: ${props => props.rowhighlightcolor || 'transparent'};
  &:not(.MuiTableRow-head) {
    &.active,
    &:hover {
      cursor: pointer;
    }

    &.active {
      background-color: ${props => props.theme.custom.palette.primary50};
    }

    &:hover {
      background-color: ${props => props.theme.custom.palette.info50};
    }
  }
`;

const TableBodySectionRow = styled(TableRow)``;

const StyledTableSection = styled(TableCell)<{ top: string; height: string }>`
  border: 1px solid #d4d4d4;
  padding: 0 1.6rem;
  background-color: ${props => props.theme.custom.palette.secondary50};
  position: sticky;
  height: 4rem;
  top: ${props => props.top};
  z-index: 4;
`;

const StyledTableSectionText = styled(Typography)`
  font-size: 1.6rem;
  font-style: normal;
  font-weight: 700;
  color: ${props => props.theme.custom.palette.secondary900};
`;

const StyledTableBodyCellInner = styled(Typography)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const EmptyResultRow = styled(TableRow)`
  padding: 1rem 0;
`;

const EmptyResultCell = styled(TableCell)`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${props => props.theme.custom.palette.muted800};
  border: none;
  text-align: center;
`;

const LoadMoreRow = styled(TableRow)`
  padding: 1rem 0;
`;

const LoadMoreTableCell = styled(TableCell)`
  border-bottom: none;
  text-align: center;
`;

const Resizer = styled.div`
  width: 2px;
  cursor: col-resize;

  display: block;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  border-right: 2px solid transparent;
  user-select: none;
  &:hover {
    border-color: #ccc;
  }

  &.active {
    border-color: #517ea5;
    width: 2px;
  }
`;

const StyledOrderStatusLabel = styled(OrderStatusLabel)`
  font-size: 1.8rem;
`;

const StyledLoadMoreButton = styled(Button)``;

type TableComponents =
  | 'Table'
  | 'TableHead'
  | 'TableBodyCell'
  | 'TableBody'
  | 'TableHeaderCell'
  | 'TableSortLabel'
  | 'TableColumnLabel'
  | 'TableHeaderRow'
  | 'TableBodyRow'
  | 'TableBodySection'
  | 'TableBodyCellInner'
  | 'LoadMoreButton';

export interface TableColumn<ItemT> {
  id: string;
  dataId?: keyof ItemT;
  highlightKey?: string;
  label?: string;
  width?: number | string;
  minWidth?: number;
  order?: number;
  align?: 'right' | 'left';
  hidden?: boolean;
  sortable?: boolean;
  resizable?: boolean;
  fixed?: boolean;
  cellStyles?: string | RuleSet;
  formatter?: (
    value: any,
    item: ItemT,
    highlights?: string[] | null,
  ) => {
    value: TooltipProps['title'];
    element?: TooltipProps['children'] | null;
  };
}

export type SectionOption<ItemT> = {
  shouldRender: (currentItem: ItemT, previousItem: ItemT) => boolean;
  render: (
    currentItem: ItemT,
  ) => React.ReactElement | React.ReactElement[] | null | string | undefined;
};

export type SectionOptions<ItemT> = Record<string, SectionOption<ItemT>>;

export interface TableProps<ItemT> {
  className?: string;
  columns: TableColumn<ItemT>[];
  columnsWidth?: Record<string, number>;
  items: ItemT[];
  itemsTotalCount: number;
  itemsLoaded: boolean;
  loadItemsPending: boolean;
  loadMoreItemsPending?: boolean;
  infiniteScroll?: boolean;
  infiniteScrollOffset?: number;
  sortField?: keyof ItemT;
  sortOrder?: APISortOrder;
  onLoadMoreClicked?: () => void;
  onColumnHeaderClicked?: (columnDataId: keyof ItemT) => void;
  onRowClick?: (item: ItemT) => void;
  onBodyRowMouseEnter?: (item: ItemT) => void;
  onBodyRowMouseLeave?: (item: ItemT) => void;
  components?: {
    [key in TableComponents]?: IStyledComponent<'web', any>;
  };
  tableMinHeight?: string;
  hideHeaders?: boolean;
  headerRowHeight?: string;
  bodyRowHeight?: string;
  isRowActive?: (item: ItemT) => boolean;
  rowHighlightColor?: (item: ItemT) => boolean;
  onColumnSizeChanged?: (
    columnId: string,
    width: string,
    tableColumns: (TableColumn<ItemT> & {
      ref: RefObject<HTMLElement | null>;
    })[],
  ) => void;
  emptyText?: string;
  loadMoreButtonText?: string;
  hiddenColumns?: TableSettingsT['hiddenColumns'];
  autoAdjustColumnsWidth?: boolean;
  sectionOptions?: SectionOptions<ItemT>;
  highlightSearchResults?: boolean;
  onScrollEnd?: (scrollPosition: number) => void;
  scrollPosition?: number;
}

export const LastColumnCellStyles = css`
  &:before {
    display: none;
  }
`;

const lastColumn: TableColumn<any> = {
  id: 'actions',
  dataId: SPAN_COLUMN_ID,
  label: '',
  width: '',
  hidden: false,
  order: 1,
  align: 'right',
  sortable: false,
  cellStyles: LastColumnCellStyles,
};

export const ordersTableSectionOptions = {
  [OrdersTableColumnIds.DeliveryDate]: {
    shouldRender: (currentItem: APIOrder, previousItem: APIOrder) => {
      return (
        !previousItem ||
        !dayjs(currentItem.deliveryDate).isSame(previousItem.deliveryDate)
      );
    },
    render: (currentItem: APIOrder) =>
      getDateSeparator(currentItem.deliveryDate, i18n.t),
  },
  'company.name': {
    shouldRender: (currentItem: APIOrder, previousItem?: APIOrder) =>
      currentItem.company.id !== previousItem?.company?.id,
    render: (currentItem: APIOrder) => currentItem.company.name,
  },
  'project.name': {
    shouldRender: (currentItem: APIOrder, previousItem?: APIOrder) =>
      currentItem.project.id !== previousItem?.project?.id,
    render: (currentItem: APIOrder) => currentItem.project.name,
  },
  orderStatus: {
    shouldRender: (currentItem: APIOrder, previousItem?: APIOrder) =>
      currentItem.orderStatus !== previousItem?.orderStatus,
    render: (currentItem: APIOrder) => (
      <StyledOrderStatusLabel status={currentItem.orderStatus} />
    ),
  },
};

const getColumnWidth = <T,>(
  column: Pick<TableColumn<any>, 'id' | 'minWidth' | 'width'>,
  columnsWidth: TableProps<T>['columnsWidth'],
) => {
  return columnsWidth?.[column.id] || column.width || column.minWidth;
};

export const Table = <
  ItemT extends {
    id: number | string;
    highlights?: Record<string, string[] | null>;
  },
>({
  className,
  columns,
  items,
  itemsTotalCount,
  itemsLoaded,
  loadItemsPending,
  loadMoreItemsPending,
  sortField,
  sortOrder,
  onLoadMoreClicked,
  onColumnHeaderClicked,
  onRowClick,
  onBodyRowMouseEnter,
  onBodyRowMouseLeave,
  components = {},
  hideHeaders,
  tableMinHeight = '0',
  headerRowHeight = '6.6rem',
  bodyRowHeight = '5.7rem',
  isRowActive,
  rowHighlightColor,
  emptyText,
  loadMoreButtonText,
  infiniteScroll = false,
  hiddenColumns = [],
  onColumnSizeChanged,
  infiniteScrollOffset = INFINITE_SCROLL_BOTTOM_OFFSET_PX,
  autoAdjustColumnsWidth = false,
  sectionOptions,
  highlightSearchResults,
  onScrollEnd,
  scrollPosition,
  columnsWidth,
}: TableProps<ItemT>) => {
  const TableComponent: React.ElementType = components.Table || StyledTable;
  const TableHeaderRowComponent: React.ElementType =
    components.TableHeaderRow || StyledTableRow;
  const TableBodyRowComponent: React.ElementType =
    components.TableBodyRow || StyledTableRow;
  const TableBodySectionComponent: React.ElementType =
    components.TableBodySection || StyledTableSection;
  const TableHeadComponent: React.ElementType =
    components.TableHead || TableHead;
  const TableBodyComponent: React.ElementType =
    components.TableBody || StyledTableBody;
  const TableHeaderCellComponent: React.ElementType =
    components.TableHeaderCell || StyledTableHeaderCell;
  const TableSortLabelComponent: React.ElementType =
    components.TableSortLabel || TableSortLabel;
  const TableColumnLabelComponent: React.ElementType =
    components.TableColumnLabel || StyledTableColumnLabel;
  const TableBodyCellComponent: React.ElementType =
    components.TableBodyCell || StyledTableBodyCell;
  const TableBodyCellInnerComponent: React.ElementType =
    components.TableBodyCellInner || StyledTableBodyCellInner;
  const LoadMoreButtonComponent: React.ElementType =
    components.LoadMoreButton || StyledLoadMoreButton;

  const tableColumns = useMemo(() => {
    return columns.map(it => ({
      ...it,
      ref: React.createRef<HTMLElement>(),
    }));
  }, [columns]);

  const [tableWidth, setTableWidth] = useState<null | number>(null);
  const [tableHeight, setTableHeight] = useState<null | number>(null);

  const tableRef = useCallbackRef<HTMLDivElement | null>(null, tableEl => {
    if (tableEl) {
      setTableWidth(tableEl.getBoundingClientRect().width);
    }
  });
  const isTableEmpty = items.length === 0;

  const showEmptyText =
    items.length === 0 && itemsLoaded && !loadItemsPending && emptyText;

  const hasLoadMoreButton =
    onLoadMoreClicked &&
    itemsLoaded &&
    itemsTotalCount > items.length &&
    items.length !== 0;

  // creating ref for always having the actual
  // value inside `onScrollToBottom` callback,
  const hasLoadMoreButtonRef = useRef(hasLoadMoreButton);
  hasLoadMoreButtonRef.current = hasLoadMoreButton;

  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const isLoadingMoreRef = useRef<typeof loadMoreItemsPending>(false);

  const onScrollToBottom = () => {
    if (hasLoadMoreButtonRef.current && !isLoadingMoreRef.current) {
      onLoadMoreClicked?.();
      // using ref to instantly update this value and
      // avoid bugs with the late update when using similar redux value
      isLoadingMoreRef.current = true;
    }
  };

  const onScrollToBottomDebounced = _.throttle(
    onScrollToBottom,
    INFINITE_SCROLL_DEBOUNCE_TIME_MS,
  );

  useEffect(() => {
    // sync the local ref value with the latest redux`loadMoreItemsPending`
    // when it's finally updated
    isLoadingMoreRef.current = loadMoreItemsPending;
  }, [loadMoreItemsPending]);

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.getBoundingClientRect().height);
    }
  }, [items.length, tableRef]);

  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    onScrollToBottomDebounced,
    {
      offset: infiniteScrollOffset,
      triggerOnNoScroll: false,
    },
  );

  useEffect(() => {
    if (scrollRef) {
      scrollRef.current?.scrollTo({ top: scrollPosition });
    }
    // only on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollPosition === 0) {
      scrollRef.current?.scrollTo({ top: 0 });
    }
    // react only when resetting to 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition]);

  useEffect(() => {
    let onScrollStopDebounced = _.noop;
    if (onScrollEnd && scrollRef?.current) {
      onScrollStopDebounced = _.debounce(event => {
        onScrollEnd(event.target.scrollTop);
      }, 500);
      scrollRef.current?.addEventListener?.('scroll', onScrollStopDebounced);
    }
    return () =>
      scrollRef &&
      // eslint-disable-next-line react-hooks/exhaustive-deps
      scrollRef?.current?.removeEventListener?.(
        'scroll',
        onScrollStopDebounced,
      );
  }, [scrollRef, onScrollEnd]);

  const visibleColumns = useMemo(() => {
    return hiddenColumns?.length
      ? tableColumns.reduce<typeof tableColumns>((result, it) => {
          return hiddenColumns.includes(it.id) ? result : [...result, it];
        }, [])
      : tableColumns;
  }, [tableColumns, hiddenColumns]);

  const fullWidthColspan = useMemo(() => {
    const isTableResizable = _.some(columns, { resizable: true });
    // resizable table has one extra column for smooth resize
    return (
      _.reject(visibleColumns, { hidden: true }).length +
      (isTableResizable ? 1 : 0)
    );
  }, [columns, visibleColumns]);

  const onColumnMouseDown = (index: string | null) => {
    setActiveColumn(index);
  };

  const onResizeMouseMove = useCallback(
    (event: MouseEvent) => {
      tableColumns.forEach(it => {
        if (it.id == activeColumn && it.ref.current) {
          const width =
            event.clientX - it.ref.current.getBoundingClientRect().left;

          it.ref.current.style.width =
            Math.max(
              it.minWidth || 0,
              width < COLUMN_RESIZER_WIDTH ? COLUMN_RESIZER_WIDTH : width,
            ) + 'px';
        }
      });
    },
    [activeColumn, tableColumns],
  );

  const sortedColumns = useMemo(() => {
    const columns = _.orderBy(visibleColumns, 'order');
    const isTableResizable = _.some(columns, { resizable: true });

    // trick: if all width are set, then browser will autosize columns
    // therefore we add one empty column without width, this will make resize smooth
    // and size other columns will not be affected
    return isTableResizable
      ? [...columns, { ...lastColumn, ref: React.createRef<HTMLDivElement>() }]
      : columns;
  }, [visibleColumns]);

  const clearListeners = useCallback(() => {
    window.removeEventListener('mousemove', onResizeMouseMove);
    window.removeEventListener('mouseup', clearListeners);
  }, [onResizeMouseMove]);

  const onResizeMouseUp = useCallback(() => {
    if (activeColumn) {
      const foundColumn = tableColumns.find(it => it.id === activeColumn);
      foundColumn?.ref?.current &&
        onColumnSizeChanged?.(
          activeColumn,
          foundColumn.ref.current.style.width,
          tableColumns,
        );
    }
    setActiveColumn(null);
    clearListeners();
  }, [activeColumn, clearListeners, tableColumns, onColumnSizeChanged]);

  useEffect(() => {
    if (autoAdjustColumnsWidth && tableWidth) {
      const totalColumnsWidth = visibleColumns.reduce((result, column) => {
        return result + Number(getColumnWidth(column, columnsWidth));
      }, 0);
      if (totalColumnsWidth < tableWidth) {
        _.forEach(visibleColumns, column => {
          if (column.ref.current) {
            column.ref.current.style.width =
              Math.max(
                column.minWidth || 0,
                (Number(getColumnWidth(column, columnsWidth)) *
                  (tableWidth - SCROLL_WIDTH)) /
                  totalColumnsWidth,
              ) + 'px';
          }
        });
      }
    }
  }, [tableWidth, autoAdjustColumnsWidth, visibleColumns, columnsWidth]);

  useEffect(() => {
    if (activeColumn !== null) {
      window.addEventListener('mousemove', onResizeMouseMove);
      window.addEventListener('mouseup', onResizeMouseUp);
    }

    return () => {
      clearListeners();
    };
  }, [activeColumn, onResizeMouseMove, onResizeMouseUp, clearListeners]);

  return (
    <TableWrapper className={className} minheight={tableMinHeight}>
      <StyledTableContainer ref={infiniteScroll ? scrollRef : null}>
        <TableComponent stickyHeader aria-label="sticky table" ref={tableRef}>
          <TableHeadComponent>
            <TableHeaderRowComponent>
              {sortedColumns.map(column =>
                column.hidden ? null : (
                  <TableHeaderCellComponent
                    id={`header-${column.id}`}
                    key={column.id}
                    ref={column.ref}
                    align={column.align}
                    style={{
                      width: getColumnWidth(column, columnsWidth),
                      ...(column.fixed && {
                        ...fixedStyles,
                        top: 0,
                        zIndex: 4,
                      }),
                    }}
                    headerheight={hideHeaders ? '0' : headerRowHeight}
                  >
                    {hideHeaders ? null : onColumnHeaderClicked &&
                      column.sortable !== false ? (
                      <TableSortLabelComponent
                        id={`sort-${column.id}`}
                        hideSortIcon={!column.dataId || isTableEmpty}
                        active={
                          !isTableEmpty &&
                          itemsLoaded &&
                          sortField === column.dataId
                        }
                        style={isTableEmpty ? emptyTableSortHeaderStyles : {}}
                        direction={sortOrder?.toLowerCase()}
                        onClick={
                          !isTableEmpty && column.dataId
                            ? () =>
                                onColumnHeaderClicked?.(
                                  column.dataId as keyof ItemT,
                                )
                            : undefined
                        }
                      >
                        {column.label || ''}
                      </TableSortLabelComponent>
                    ) : (
                      <TableColumnLabelComponent id={`label-${column.id}`}>
                        {column.label || ''}
                      </TableColumnLabelComponent>
                    )}
                    <Resizer
                      style={{
                        display: column.resizable ? 'block' : 'none',
                        height: items.length ? tableHeight || 0 : '100%',
                      }}
                      onMouseDown={() => onColumnMouseDown(column.id)}
                      className={activeColumn === column.id ? 'active' : 'idle'}
                    />
                  </TableHeaderCellComponent>
                ),
              )}
            </TableHeaderRowComponent>
          </TableHeadComponent>
          {!loadItemsPending && (
            <TableBodyComponent>
              {items.map((item, index) => {
                const section = sectionOptions?.[(sortField || '').toString()];
                const shouldRenderSection = section?.shouldRender(
                  item,
                  items?.[index - 1],
                );
                const sectionElement = section?.render?.(item);

                return [
                  ...[
                    shouldRenderSection
                      ? [
                          <TableBodySectionRow key={`section-${item.id}`}>
                            <TableBodySectionComponent
                              height={bodyRowHeight}
                              top={hideHeaders ? '0' : headerRowHeight}
                              colSpan={fullWidthColspan}
                            >
                              {isTitleString(sectionElement) ? (
                                <StyledTableSectionText>
                                  {sectionElement}
                                </StyledTableSectionText>
                              ) : (
                                sectionElement
                              )}
                            </TableBodySectionComponent>
                          </TableBodySectionRow>,
                        ]
                      : [],
                  ],
                  <TableBodyRowComponent
                    id={`row-${item.id}`}
                    key={item.id}
                    className={classNames({
                      active: isRowActive?.(item),
                    })}
                    onClick={() => onRowClick?.(item)}
                    onMouseEnter={() => onBodyRowMouseEnter?.(item)}
                    onMouseLeave={() => onBodyRowMouseLeave?.(item)}
                    item={item}
                    rowhighlightcolor={rowHighlightColor?.(item)}
                  >
                    {sortedColumns.map(column => {
                      if (column.hidden) {
                        return null;
                      }
                      const rawValue = column.dataId
                        ? _.get(item, column.dataId)
                        : '';
                      // @ts-ignore TODO: find out the right constraint for `anything with .toString() method`
                      const { value = rawValue?.toString(), element = null } =
                        column.formatter?.(
                          rawValue,
                          item,
                          highlightSearchResults
                            ? item.highlights?.[
                                column.highlightKey || (column.dataId as string)
                              ]
                            : undefined,
                        ) || {};

                      return (
                        <TableBodyCellComponent
                          id={`cell-${column.id}-${item.id}`}
                          key={column.id + item.id}
                          align={column.align}
                          height={bodyRowHeight}
                          cellstyles={column.cellStyles}
                          style={{
                            ...(column.fixed && {
                              ...fixedStyles,
                              backgroundColor: 'inherit',
                            }),
                          }}
                        >
                          <CellTooltip
                            placement="top-start"
                            enterDelay={TOOLTIP_APPEAR_DELAY}
                            enterNextDelay={TOOLTIP_APPEAR_DELAY}
                            title={value}
                          >
                            {element || (
                              <TableBodyCellInnerComponent>
                                {value}
                              </TableBodyCellInnerComponent>
                            )}
                          </CellTooltip>
                        </TableBodyCellComponent>
                      );
                    })}
                  </TableBodyRowComponent>,
                ];
              })}
              {showEmptyText && (
                <EmptyResultRow>
                  <EmptyResultCell colSpan={fullWidthColspan}>
                    {emptyText}
                  </EmptyResultCell>
                </EmptyResultRow>
              )}
              {hasLoadMoreButton && (
                <LoadMoreRow>
                  <LoadMoreTableCell colSpan={fullWidthColspan}>
                    <LoadMoreButtonComponent
                      loading={loadMoreItemsPending}
                      disabled={loadMoreItemsPending}
                      variant="outlined"
                      color="secondary"
                      onClick={onLoadMoreClicked}
                    >
                      {loadMoreButtonText}
                    </LoadMoreButtonComponent>
                  </LoadMoreTableCell>
                </LoadMoreRow>
              )}
            </TableBodyComponent>
          )}
          {/* Skeleton loading state */}
          {loadItemsPending && (
            <TableBodyComponent>
              {Array.from(
                { length: Math.max(30, items.length) },
                (_, index) => {
                  const skeletonItem = { id: `skeleton-${index}` };
                  return [
                    <TableBodyRowComponent
                      id={`row-${skeletonItem.id}`}
                      key={skeletonItem.id}
                      item={skeletonItem}
                    >
                      {sortedColumns.map(column => {
                        if (column.hidden) {
                          return null;
                        }
                        return (
                          <TableBodyCellComponent
                            id={`cell-${column.id}-${skeletonItem.id}`}
                            key={column.id + skeletonItem.id}
                            align={column.align}
                            height={bodyRowHeight}
                            cellstyles={column.cellStyles}
                            style={{
                              ...(column.fixed && {
                                ...fixedStyles,
                                backgroundColor: 'inherit',
                              }),
                            }}
                          >
                            <ContentLoader height={bodyRowHeight}>
                              <rect x="0" y="10" width="170" height="20" />
                            </ContentLoader>
                          </TableBodyCellComponent>
                        );
                      })}
                    </TableBodyRowComponent>,
                  ];
                },
              )}
            </TableBodyComponent>
          )}
        </TableComponent>
      </StyledTableContainer>
    </TableWrapper>
  );
};
